const SCROLL_MODE_BASE_COLUMN_WIDTH_PX = 200;

export function parseTrack(size: string | undefined): {
  fr?: number;
  min?: string;
} {
  if (!size) return { fr: 1 };
  const match =
    /^(?:minmax\(\s*([^,]+?)\s*,\s*)?([0-9]*\.?[0-9]+)fr\s*\)?$/.exec(size);
  return match ? { fr: parseFloat(match[2]), min: match[1] } : {};
}

export function definedSizeToScrollPx(size: string | undefined): string {
  if (!size) return `${SCROLL_MODE_BASE_COLUMN_WIDTH_PX}px`;
  const frMatch = /^([0-9]*\.?[0-9]+)fr$/.exec(size);
  if (frMatch) {
    const fr = parseFloat(frMatch[1]);
    const px = Math.max(
      SCROLL_MODE_BASE_COLUMN_WIDTH_PX,
      Math.round(fr * SCROLL_MODE_BASE_COLUMN_WIDTH_PX),
    );
    return `${px}px`;
  }
  return size;
}

/**
 * Tracks of the fit layout for the visible columns, from each column's code
 * `size` and its `stored` resized width (`0.2fr`; resized widths sum to 1).
 * A column without a stored width (e.g. just shown) gets its code size
 * converted into the stored unit, so it takes a normal share instead of e.g.
 * `3fr` next to `0.2fr`.
 */
export function toFitTracks(
  columns: { size?: string; stored?: string }[],
): string[] {
  let storedSum = 0;
  let definedSum = 0;
  for (const { size, stored } of columns) {
    const storedFr = stored ? parseTrack(stored).fr : undefined;
    const definedFr = parseTrack(size).fr;
    if (storedFr && definedFr) {
      storedSum += storedFr;
      definedSum += definedFr;
    }
  }
  const unit = definedSum > 0 ? storedSum / definedSum : 1;

  return columns.map(({ size, stored }) => {
    const defined = parseTrack(size);
    const storedFr = stored ? parseTrack(stored).fr : undefined;
    if (!storedFr && defined.fr === undefined) return size as string;
    // ×100: stored fractions of the visible columns can sum below 1 (some
    // resized column hidden), and `fr` sums below 1 leave width unused.
    const fr = Number(
      ((storedFr ?? (defined.fr ?? 1) * unit) * 100).toFixed(2),
    );
    return defined.min ? `minmax(${defined.min}, ${fr}fr)` : `${fr}fr`;
  });
}
